import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { TUnlinkData } from './types';

import { ECrowdCellConnectionType } from '@enums/common';
import { ECrowdCellFields } from './enums';

import colors from '@constants/colors';
import { columnSizes } from '@constants/table';
import {
	index,
	manageSite,
	siteProfile,
} from './constants';

import LinkMenu from './LinkMenu/LinkMenu';

import styles from '@components/common/Table/Table.module.scss';

interface IGetColumnsProps {
	navigate: NavigateFunction;
	openUnlinkConfirm: () => void;
	setSelectedCrowdCell: React.Dispatch<React.SetStateAction<string | null>>;
	setUnlinkData: React.Dispatch<React.SetStateAction<TUnlinkData | null>>;
	linkActions: { label: string; name: string; action: () => void }[];
	linkTooltipContent: string;
	unlinkTooltipContent: string;
}

export const getColumns = ({
	navigate,
	openUnlinkConfirm,
	setUnlinkData,
	setSelectedCrowdCell,
	linkActions,
	linkTooltipContent,
	unlinkTooltipContent,
}: IGetColumnsProps) => [
	{
		header: 'FLEX-5G ID',
		accessorKey: ECrowdCellFields.systemId,
		cell: ({ getValue }: CellContext<any, any>) => {
			const systemId = getValue();

			return <AdaptiveTableCell text={systemId} />;
		},
	},
	{
		header: 'SITE_ID',
		accessorKey: ECrowdCellFields.siteId,
		size: columnSizes.size190,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const { siteDbId } = original;
			const siteId = getValue();
			const openSiteProfileHandler = () => navigate(`${index}${manageSite}${siteProfile}/${siteDbId}`);

			return (
				siteId && (
					<div className={styles.installedOnAsset}>
						<div onClick={openSiteProfileHandler}>
							<span>{siteId}</span>&nbsp;{Icons.locationLink()}
						</div>
					</div>
				)
			);
		},
	},
	{
		header: 'STATUS',
		accessorKey: ECrowdCellFields.status,
		cell: ({ getValue }: CellContext<any, any>) => {
			const { title, name } = getValue();

			return (
				<AdaptiveTableCell
					styleInline={{ color: name === ECrowdCellConnectionType.OFFLINE ? colors.pink50 : colors.green60 }}
					text={title}
				/>
			);
		},
	},
	{
		header: '4G (enb_ID)',
		accessorKey: ECrowdCellFields.enodeBId,
		cell: ({ getValue }: CellContext<any, any>) => {
			const enodeBId = getValue();

			return <AdaptiveTableCell text={enodeBId} />;
		},
	},
	{
		header: '5G (gnb_ID)',
		accessorKey: ECrowdCellFields.gnodeBId,
		cell: ({ getValue }: CellContext<any, any>) => {
			const gnodeBId = getValue();

			return <AdaptiveTableCell text={gnodeBId} />;
		},
	},
	{
		header: 'MME',
		accessorKey: ECrowdCellFields.mme,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const mme = getValue();
			const text = mme ? 'MME' : '';

			return <AdaptiveTableCell text={text} />;
		},
	},
	{
		header: 'IMS',
		accessorKey: ECrowdCellFields.ims,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const ims = getValue();
			const text = ims ? 'IMS' : '';

			return <AdaptiveTableCell text={text} />;
		},
	},
	{
		header: 'LATTITUDE',
		accessorKey: ECrowdCellFields.lat,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const lat = getValue();

			return <AdaptiveTableCell text={lat} />;
		},
	},
	{
		header: 'LONGITUDE',
		accessorKey: ECrowdCellFields.lng,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const lng = getValue();

			return <AdaptiveTableCell text={lng} />;
		},
	},
	{
		header: 'FIRST SEEN',
		accessorKey: ECrowdCellFields.firstSeen,
		cell: ({ getValue }: CellContext<any, any>) => {
			const firstSeen = getValue();

			return <AdaptiveTableCell text={firstSeen} />;
		},
	},
	{
		header: 'LAST SEEN',
		accessorKey: ECrowdCellFields.lastSeen,
		cell: ({ getValue }: CellContext<any, any>) => {
			const lastSeen = getValue();

			return <AdaptiveTableCell text={lastSeen} />;
		},
	},
	{
		header: () => <div className={styles.centerColumnHeader}>Actions</div>,
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size90,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const { siteDbId, id, siteId } = original;

			const unlinkHandler = () => {
				if (!siteDbId || !id) return;

				openUnlinkConfirm();
				setUnlinkData({ siteDbId, crowdCellId: id });
			};

			const handleIdSet = () => setSelectedCrowdCell(id);

			return !siteId ?
				<LinkMenu
					actions={linkActions}
					selectHandler={handleIdSet}
					tooltipContent={linkTooltipContent}
				/>
				:
				<Actions
					gridNumberOfColumns={1}
					isLink
					linkHandler={unlinkHandler}
					linkTooltip={unlinkTooltipContent}
					linkedToSite
				/>
				;
		},
	},
];
